import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, AlertTriangle, Clock, Loader2, Scale, FileText } from 'lucide-react'
import { api } from '../lib/api'
import {
  CourtFilingKindLabel,
  CourtNotificationTypeLabel,
  type DeadlineItem,
  type DeadlineListResponse,
} from '@toga/shared'

type Bucket = 'OVERDUE' | 'TODAY' | 'THREE' | 'WEEK' | 'MONTH' | 'LATER'

const BUCKET_LABEL: Record<Bucket, string> = {
  OVERDUE: 'Vencidos',
  TODAY: 'Hoy',
  THREE: 'Próximos 3 días',
  WEEK: 'Próximos 7 días',
  MONTH: 'Próximos 30 días',
  LATER: 'Más adelante',
}

const BUCKET_STYLES: Record<Bucket, string> = {
  OVERDUE: 'border-rose-500/40 bg-rose-500/5',
  TODAY: 'border-amber-500/40 bg-amber-500/5',
  THREE: 'border-orange-500/40 bg-orange-500/5',
  WEEK: 'border-blue-500/30 bg-blue-500/5',
  MONTH: 'border-base bg-transparent',
  LATER: 'border-base bg-transparent',
}

function bucketOf(item: DeadlineItem): Bucket {
  if (item.daysUntil < 0) return 'OVERDUE'
  if (item.daysUntil === 0) return 'TODAY'
  if (item.daysUntil <= 3) return 'THREE'
  if (item.daysUntil <= 7) return 'WEEK'
  if (item.daysUntil <= 30) return 'MONTH'
  return 'LATER'
}

export default function Deadlines() {
  const [items, setItems] = useState<DeadlineItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [withinDays, setWithinDays] = useState(90)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<DeadlineListResponse>(`/deadlines?withinDays=${withinDays}`)
      setItems(resp.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar')
    } finally {
      setLoading(false)
    }
  }, [withinDays])

  useEffect(() => {
    void reload()
  }, [reload])

  const buckets = useMemo(() => {
    const grouped = new Map<Bucket, DeadlineItem[]>()
    for (const i of items) {
      const b = bucketOf(i)
      const arr = grouped.get(b) ?? []
      arr.push(i)
      grouped.set(b, arr)
    }
    const order: Bucket[] = ['OVERDUE', 'TODAY', 'THREE', 'WEEK', 'MONTH', 'LATER']
    return order
      .filter((b) => grouped.has(b))
      .map((b) => ({ bucket: b, items: grouped.get(b)! }))
  }, [items])

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <Clock size={20} className="text-toga-300" />
            Plazos
          </h1>
          <p className="text-xs t-muted">
            {items.length} pendiente{items.length !== 1 ? 's' : ''} de cumplir
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs t-muted">
          <span>Horizonte:</span>
          <select
            value={withinDays}
            onChange={(e) => setWithinDays(Number(e.target.value))}
            className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary"
          >
            <option value={30}>30 días</option>
            <option value={60}>60 días</option>
            <option value={90}>90 días</option>
            <option value={180}>6 meses</option>
            <option value={365}>1 año</option>
          </select>
        </div>
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {loading ? (
        <div className="t-muted flex items-center gap-2 text-sm">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="surface p-12 text-center">
          <Clock size={32} className="mx-auto t-faint mb-2" />
          <p className="t-muted text-sm">No hay plazos próximos en este horizonte.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {buckets.map(({ bucket, items: list }) => (
            <section key={bucket} className={`surface p-4 border ${BUCKET_STYLES[bucket]}`}>
              <h3 className="font-semibold t-primary mb-3 flex items-center gap-2 text-sm">
                {bucket === 'OVERDUE' && <AlertTriangle size={14} className="text-rose-300" />}
                {BUCKET_LABEL[bucket]}
                <span className="text-xs t-faint font-normal">({list.length})</span>
              </h3>
              <ul className="divide-y divide-base/40">
                {list.map((item) => (
                  <li key={`${item.source}-${item.id}`} className="py-2 flex items-center gap-3">
                    <div className="shrink-0">
                      {item.source === 'NOTIFICATION' ? (
                        <Scale size={14} className="text-toga-300" />
                      ) : (
                        <FileText size={14} className="text-blue-300" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link
                          to={`/cases/${item.caseId}`}
                          className="text-sm t-primary hover:underline truncate"
                        >
                          {item.title}
                        </Link>
                        <span className="text-[10px] t-faint">
                          {item.source === 'NOTIFICATION'
                            ? (CourtNotificationTypeLabel[
                                item.subtype as keyof typeof CourtNotificationTypeLabel
                              ] ?? item.subtype)
                            : (CourtFilingKindLabel[
                                item.subtype as keyof typeof CourtFilingKindLabel
                              ] ?? item.subtype)}
                        </span>
                      </div>
                      <div className="text-[11px] t-muted">
                        <span className="font-mono text-toga-300">{item.caseCode}</span>
                        {' · '}
                        <span>{item.caseTitle}</span>
                      </div>
                    </div>
                    <div className="text-right text-xs t-secondary">
                      <div className="font-mono">
                        {new Date(item.date).toLocaleDateString('es-ES')}
                      </div>
                      <div
                        className={
                          item.isOverdue
                            ? 'text-rose-300 font-semibold'
                            : item.daysUntil <= 3
                              ? 'text-amber-300'
                              : 't-faint'
                        }
                      >
                        {item.isOverdue
                          ? `Vencido hace ${Math.abs(item.daysUntil)} d`
                          : item.daysUntil === 0
                            ? 'Hoy'
                            : `En ${item.daysUntil} d`}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
